import { FormField } from '@cp/ds/src/components/form';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { LocationSuggestion } from '@common/model/location/locationSuggestion';
import { ADDRESS_SEARCH_QUERY_MIN_LENGTH } from '@common/const/location/address';
import { useFormAppContext } from '@cp/utils/form/formAppContext';
import { AutocompleteInputProps } from '@cp/ds/src/components/form/components/field/autocomplete';
import { CITY_SEARCH_FORM_FIELD_NAME_DEFAULT } from '../citySearchFormField';
import { AddressSearchFormFieldProps } from './model';
import { NAME_DEFAULT } from './const';

export { NAME_DEFAULT as ADDRESS_SEARCH_FORM_FIELD_NAME_DEFAULT } from './const';

export * from './model';

const getKey = (value: LocationSuggestion) => {
  return `${value.location}-${value.address}`;
};

const getLabel = (suggestion: LocationSuggestion) => suggestion.address;

export const AddressSearchFormField: React.FC<AddressSearchFormFieldProps> = ({
  name = NAME_DEFAULT,
  cityName = CITY_SEARCH_FORM_FIELD_NAME_DEFAULT,
  ...fieldProps
}) => {
  const form = useFormContext();
  const formAppContext = useFormAppContext<CityViewModel, LocationSuggestion>();

  React.useEffect(() => {
    const subscription = form.watch((values, info) => {
      if (info.type !== 'change') {
        return;
      }

      /**
       * Reset on city changes
       */
      if (info.name === cityName) {
        form.setValue(name, null);
      }
    });
    return () => subscription.unsubscribe();
  }, [cityName, form, name]);

  const handleRequestSuggestions = React.useCallback<AutocompleteInputProps['onRequestSuggestions']>(
    (query) => {
      const city = form.getValues(cityName) as CityViewModel | undefined;

      if (!formAppContext.onSearchLocation || !city) {
        return Promise.resolve([]);
      }

      return formAppContext.onSearchLocation(query, { cityId: city.id });
    },
    [cityName, form, formAppContext],
  );

  if (!formAppContext.onSearchLocation) {
    // eslint-disable-next-line quotes
    throw new Error("Parameter 'onSearchLocation' in FormAppContext should be provided when AddressSearchFormField is used");
  }

  return (
    <FormField
      getKey={getKey}
      getLabel={getLabel}
      name={name}
      onRequestSuggestions={handleRequestSuggestions}
      queryMinLengthToRequestSuggestions={ADDRESS_SEARCH_QUERY_MIN_LENGTH}
      type="autocomplete"
      {...fieldProps}
    />
  );
};
